import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

/**
 * Armazenamento (S3 — uploads) — status da integração usada por WMS/purchasing
 * e formulário para cadastrar/atualizar as credenciais do bucket.
 *
 * POR QUE EXISTE: sem S3 configurado, os uploads caem na pasta local do
 * servidor (fallback silencioso). Isso é uma bomba-relógio em qualquer
 * ambiente sem disco persistente — este card deixa esse estado visível no
 * Health e permite corrigir sem SSH.
 */
const Chip = ({ tone, children }) => {
    const tons = {
        ok: 'bg-success-soft text-success-text dark:bg-success-900/30 dark:text-success-300',
        warn: 'bg-warning-soft text-warning-text dark:bg-warning-900/30 dark:text-warning-300',
        off: 'bg-mono-100 text-mono-600 dark:bg-mono-800 dark:text-mono-300',
        bad: 'bg-danger-soft text-danger-text dark:bg-danger-900/30 dark:text-danger-300',
    };
    return (
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${tons[tone] || tons.off}`}>
            {children}
        </span>
    );
};

const SOURCE_LABEL = {
    integration: 'cadastrado via UI',
    env: 'variável de ambiente do servidor',
};

const FORM_INICIAL = { bucket: '', region: '', access_key_id: '', secret_access_key: '' };

const StorageCard = () => {
    const [status, setStatus] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    const [aviso, setAviso] = useState(null);
    const [mostrarForm, setMostrarForm] = useState(false);
    const [form, setForm] = useState(FORM_INICIAL);
    const [salvando, setSalvando] = useState(false);
    const [ocupado, setOcupado] = useState(false);

    const carregar = useCallback(async () => {
        try {
            setErro(null);
            const res = await api.get('/integrations/storage/status');
            setStatus(res?.data ?? res);
        } catch (e) {
            setErro(
                e?.status === 403
                    ? 'Sem permissão para ver o status de armazenamento (requer developer).'
                    : e?.message || 'Não foi possível ler o status do armazenamento.',
            );
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const salvar = async (e) => {
        e.preventDefault();
        setSalvando(true);
        setAviso(null);
        try {
            await api.put('/integrations/storage', {
                bucket: form.bucket.trim(),
                region: form.region.trim(),
                access_key_id: form.access_key_id.trim(),
                secret_access_key: form.secret_access_key,
            });
            setAviso('Integração S3 salva.');
            setForm(FORM_INICIAL);
            setMostrarForm(false);
            await carregar();
        } catch (e2) {
            setAviso(
                e2?.status === 403
                    ? 'Sem permissão para alterar a integração (requer developer).'
                    : e2?.message || 'Não foi possível salvar a integração.',
            );
        } finally {
            setSalvando(false);
        }
    };

    const desconectar = async () => {
        if (
            !window.confirm(
                'Desconectar a integração S3?\n\nOs uploads passam a ser gravados na pasta local do servidor.',
            )
        ) {
            return;
        }
        setOcupado(true);
        setAviso(null);
        try {
            await api.post('/integrations/storage/disconnect');
            setAviso('Integração S3 desconectada.');
            await carregar();
        } catch (e) {
            setAviso(
                e?.status === 403
                    ? 'Sem permissão para desconectar (requer developer).'
                    : e?.message || 'Não foi possível desconectar.',
            );
        } finally {
            setOcupado(false);
        }
    };

    const modoLocal = status?.mode === 'local_folder';
    const integrado = status?.integrated === true;

    return (
        <div className="overflow-hidden rounded-xl border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950">
            <div className="flex items-center justify-between border-b border-mono-100 px-4 py-2.5 dark:border-mono-900">
                <p className="text-sm font-semibold text-black dark:text-white">Armazenamento (S3 — uploads)</p>
                <button
                    type="button"
                    onClick={carregar}
                    className="rounded-md border border-mono-300 px-2 py-0.5 text-[10px] font-semibold text-mono-600 transition hover:border-info-500 hover:text-info-600 dark:border-mono-700 dark:text-mono-300"
                >
                    Verificar
                </button>
            </div>

            {aviso ? (
                <p className="border-b border-mono-100 bg-info-soft/40 px-4 py-2 text-[11px] text-info-text dark:border-mono-900 dark:bg-info-900/20 dark:text-info-300">
                    {aviso}
                </p>
            ) : null}

            {carregando ? (
                <p className="px-4 py-6 text-center text-xs text-mono-400">Consultando…</p>
            ) : erro ? (
                <p className="px-4 py-6 text-center text-xs text-danger-600 dark:text-danger-400">{erro}</p>
            ) : (
                <div className="px-4 py-3">
                    {modoLocal ? (
                        <div className="rounded-lg border border-warning-500/40 bg-warning-soft px-3 py-2 text-[11px] text-warning-text dark:bg-warning-900/20 dark:text-warning-300">
                            <p className="font-semibold">
                                {status?.warning ||
                                    'Integração S3 não configurada — uploads estão sendo gravados na pasta local de uploads do servidor.'}
                            </p>
                            {status?.local_uploads_dir ? (
                                <p className="mt-0.5 font-mono text-[10px] opacity-90">{status.local_uploads_dir}</p>
                            ) : null}
                        </div>
                    ) : null}

                    <div className="mt-2 flex flex-wrap items-center gap-3">
                        <div className="min-w-[200px] flex-1">
                            <div className="flex items-center gap-2">
                                {integrado ? <Chip tone="ok">integrado</Chip> : <Chip tone="off">não integrado</Chip>}
                                {status?.source ? (
                                    <Chip tone="off">{SOURCE_LABEL[status.source] || status.source}</Chip>
                                ) : null}
                            </div>
                            {integrado ? (
                                <p className="mt-1 text-[11px] text-mono-500">
                                    bucket <strong>{status.bucket || '—'}</strong> · região {status.region || '—'} ·
                                    chave {status.access_key_id_masked || '—'}
                                </p>
                            ) : null}
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setMostrarForm((v) => !v)}
                                className="rounded-md border border-info-500 px-2.5 py-1 text-[11px] font-semibold text-info-600 transition hover:bg-info-500 hover:text-white dark:text-info-300"
                            >
                                {mostrarForm ? 'Fechar' : integrado ? 'Atualizar' : 'Configurar'}
                            </button>
                            {status?.source === 'integration' ? (
                                <button
                                    type="button"
                                    disabled={ocupado}
                                    onClick={desconectar}
                                    className="rounded-md border border-mono-300 px-2.5 py-1 text-[11px] font-semibold text-mono-600 transition hover:border-danger-500 hover:text-danger-600 disabled:cursor-not-allowed disabled:opacity-40 dark:border-mono-700 dark:text-mono-300"
                                >
                                    {ocupado ? '…' : 'Desconectar'}
                                </button>
                            ) : null}
                        </div>
                    </div>

                    {mostrarForm ? (
                        <form
                            onSubmit={salvar}
                            className="mt-3 grid grid-cols-1 gap-2 rounded-lg border border-mono-200 p-3 sm:grid-cols-2 dark:border-mono-800"
                        >
                            <label className="text-[11px] text-mono-500">
                                Bucket
                                <input
                                    required
                                    value={form.bucket}
                                    onChange={(e) => setForm((f) => ({ ...f, bucket: e.target.value }))}
                                    className="mt-0.5 w-full rounded-md border border-mono-300 px-2 py-1 text-xs text-mono-900 outline-none focus:ring-1 focus:ring-info-500 dark:border-mono-700 dark:bg-mono-900 dark:text-mono-100"
                                />
                            </label>
                            <label className="text-[11px] text-mono-500">
                                Região
                                <input
                                    required
                                    value={form.region}
                                    onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
                                    placeholder="us-east-1"
                                    className="mt-0.5 w-full rounded-md border border-mono-300 px-2 py-1 text-xs text-mono-900 outline-none focus:ring-1 focus:ring-info-500 dark:border-mono-700 dark:bg-mono-900 dark:text-mono-100"
                                />
                            </label>
                            <label className="text-[11px] text-mono-500">
                                Access Key ID
                                <input
                                    required
                                    value={form.access_key_id}
                                    onChange={(e) => setForm((f) => ({ ...f, access_key_id: e.target.value }))}
                                    className="mt-0.5 w-full rounded-md border border-mono-300 px-2 py-1 font-mono text-xs text-mono-900 outline-none focus:ring-1 focus:ring-info-500 dark:border-mono-700 dark:bg-mono-900 dark:text-mono-100"
                                />
                            </label>
                            <label className="text-[11px] text-mono-500">
                                Secret Access Key
                                <input
                                    required
                                    type="password"
                                    value={form.secret_access_key}
                                    onChange={(e) => setForm((f) => ({ ...f, secret_access_key: e.target.value }))}
                                    className="mt-0.5 w-full rounded-md border border-mono-300 px-2 py-1 font-mono text-xs text-mono-900 outline-none focus:ring-1 focus:ring-info-500 dark:border-mono-700 dark:bg-mono-900 dark:text-mono-100"
                                />
                            </label>
                            <div className="flex justify-end gap-2 sm:col-span-2">
                                <button
                                    type="submit"
                                    disabled={salvando}
                                    className="rounded-md bg-info-500 px-3 py-1 text-[11px] font-semibold text-white disabled:opacity-40"
                                >
                                    {salvando ? 'Salvando…' : 'Salvar'}
                                </button>
                            </div>
                        </form>
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default StorageCard;
